/*
 * H&H — FAQ accordion motion (sections/faq-accordion.liquid).
 *
 * The section renders native <details>/<summary>, which open and close as a
 * hard cut: on a 21-question page every open jerks the rest of the list down
 * and the reader loses their place. This element keeps the native element —
 * semantics, keyboard, find-in-page, SEO, and the no-JS fallback all stay —
 * and only takes over the paint between the two states.
 *
 * Why height and not transform: an accordion is the case where a layout
 * property is the correct one to animate. scaleY distorts the text it is
 * revealing, and clip-path cannot push the following rows down. The tween is
 * scoped to one short block per item, runs on the Web Animations API, and
 * clears its inline height on finish so nothing else that flips [open] — the
 * theme editor, Chrome's find-in-page auto-expand — inherits a stale value.
 *
 * Motion tokens match hh-brand-custom.css (--hh-ease-out, --hh-dur-*).
 * prefers-reduced-motion drops straight through to the native instant toggle.
 */

(function () {
  if (customElements.get('faq-accordion')) return;

  const EASE = 'cubic-bezier(0.23, 1, 0.32, 1)'; /* --hh-ease-out */
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Short answers should not crawl and long ones should not snap, so the
     height buys time up to a ceiling instead of every item sharing one
     duration. Closing runs shorter — an exit the reader has already decided
     on should get out of the way. */
  const duration = (height) => Math.round(Math.min(200 + height * 0.42, 340));

  const cancelAll = (element) => {
    if (element) element.getAnimations().forEach((animation) => animation.cancel());
  };

  /* Every transition claims a ticket. A cancelled tween's promise settles on a
     microtask after its replacement has already taken over, so cleanup has to
     ask "am I still the current transition?" rather than read the DOM state —
     by then the state belongs to whoever came next. */
  const claim = (item) => (item.faqSeq = (item.faqSeq || 0) + 1);

  class FaqAccordion extends HTMLElement {
    connectedCallback() {
      if (this.dataset.faqBound) return;
      this.dataset.faqBound = 'true';
      this.addEventListener('click', this.onClick.bind(this));
    }

    onClick(event) {
      const summary = event.target.closest && event.target.closest('.hh-faq__question');
      if (!summary || !this.contains(summary)) return;

      /* A link inside a question keeps its own behavior. */
      if (event.target.closest('a')) return;

      const item = summary.closest('.hh-faq__item');
      const wrap = item && item.querySelector('.hh-faq__answer-wrap');
      if (!item || !wrap || reducedMotion.matches) return;

      event.preventDefault();
      /* [open] stays set for the whole close tween, so it cannot answer "is
         this row open?" on its own — a click landing mid-close would read as
         open and start the close over, and the reader who changed their mind
         would watch nothing happen. Intent lives in .is-closing. */
      if (item.open && !item.classList.contains('is-closing')) this.close(item, wrap);
      else this.open(item, wrap);
    }

    open(item, wrap) {
      const answer = wrap.firstElementChild;
      /* Mid-close: pick the tween up at the height it is currently painting. */
      const from = item.open ? wrap.getBoundingClientRect().height : 0;
      const seq = claim(item);

      cancelAll(wrap);
      cancelAll(answer);
      item.classList.remove('is-closing');
      item.open = true;
      wrap.style.height = '';

      const target = wrap.scrollHeight;
      const ms = duration(target);
      /* Hands the mark's CSS transition the same length as the height tween. */
      item.style.setProperty('--hh-faq-dur', `${ms}ms`);

      const animation = wrap.animate({ height: [`${from}px`, `${target}px`] }, { duration: ms, easing: EASE });
      wrap.style.height = `${target}px`;
      animation.finished
        .catch(() => {})
        .finally(() => {
          if (item.faqSeq !== seq) return;
          wrap.style.height = '';
        });

      if (answer) {
        answer.animate(
          { opacity: [0, 1], transform: ['translateY(-6px)', 'none'] },
          { duration: Math.round(ms * 0.8), delay: Math.round(ms * 0.2), easing: EASE, fill: 'backwards' }
        );
      }
    }

    close(item, wrap) {
      const answer = wrap.firstElementChild;
      const from = wrap.getBoundingClientRect().height;
      const ms = Math.round(duration(from) * 0.82);
      const seq = claim(item);

      cancelAll(wrap);
      cancelAll(answer);
      item.style.setProperty('--hh-faq-dur', `${ms}ms`);
      item.classList.add('is-closing');

      if (answer) {
        answer.animate({ opacity: [1, 0] }, { duration: Math.round(ms * 0.6), easing: 'ease-out', fill: 'forwards' });
      }

      const animation = wrap.animate({ height: [`${from}px`, '0px'] }, { duration: ms, easing: EASE });
      wrap.style.height = '0px';
      animation.finished
        .then(() => {
          if (item.faqSeq === seq) item.open = false;
        })
        .catch(() => {})
        .finally(() => {
          if (item.faqSeq !== seq) return;
          item.classList.remove('is-closing');
          wrap.style.height = '';
          cancelAll(answer);
        });
    }
  }

  customElements.define('faq-accordion', FaqAccordion);
})();
